import { makeAutoObservable, runInAction } from 'mobx';
import { Resource } from '../lib/types/resource';
import { Response } from '../lib/types/response';
import { isSuccess, toResource, toResourceError, isLoaded } from '../lib/utils/type-guards';
import { portfolioService } from '../services/portfolio-service';
import { PortfolioData, Asset, AssetDisplayData, BucketDisplayData } from '../types';
import { priceStore } from './price-store';

export class PortfolioStore {
  // Main portfolio data resource
  private _portfolioData: Resource<PortfolioData> = { buckets: {}, assets: {} };

  constructor() {
    makeAutoObservable(this);
    this.loadFromLocalStorage();
  }

  /**
   * Update portfolio data from auth system
   */
  updatePortfolioDataFromAuth(data: PortfolioData): void {
    runInAction(() => {
      this._portfolioData = toResource(data);
    });
  }

  /**
   * Get portfolio data resource
   */
  get portfolioData(): Resource<PortfolioData> {
    return this._portfolioData;
  }

  /**
   * Get loaded portfolio data or fallback to empty structure
   */
  get safePortfolioData(): PortfolioData {
    if (isLoaded(this._portfolioData)) {
      return this._portfolioData;
    }
    return { buckets: {}, assets: {} };
  }

  /**
   * Load portfolio data from localStorage
   */
  private async loadFromLocalStorage(): Promise<Response<void>> {
    const response = portfolioService.loadFromLocalStorage();
    
    runInAction(() => {
      if (isSuccess(response)) {
        this._portfolioData = toResource(response);
      } else {
        this._portfolioData = toResourceError(response);
      }
    });

    return isSuccess(response) ? undefined : response;
  }

  /**
   * Save portfolio data to localStorage
   */
  private async persistChanges(): Promise<Response<void>> {
    const data = this.safePortfolioData;
    return portfolioService.saveToLocalStorage(data);
  }

  // ===============================
  // PUBLIC GETTERS
  // ===============================

  get allBuckets() {
    return this.safePortfolioData.buckets;
  }

  get allAssets() {
    return this.safePortfolioData.assets;
  }

  get bucketNames() {
    return Object.keys(this.safePortfolioData.buckets);
  }

  get totalBuckets() {
    return Object.keys(this.safePortfolioData.buckets).length;
  }

  get totalAssets() {
    return Object.keys(this.safePortfolioData.assets).length;
  }

  // ===============================
  // PUBLIC METHODS
  // ===============================

  bucketExists(bucketName: string): boolean {
    return !!this.safePortfolioData.buckets[bucketName];
  }

  assetExists(assetId: string): boolean {
    return !!this.safePortfolioData.assets[assetId];
  }

  getAsset(assetId: string): Asset | null {
    return this.safePortfolioData.assets[assetId] || null;
  }

  getAssetsByBucket(bucketName: string): Asset[] {
    const bucket = this.safePortfolioData.buckets[bucketName];
    if (!bucket) return [];
    
    return bucket
      .map(assetId => this.safePortfolioData.assets[assetId])
      .filter(Boolean);
  }

  async addBucket(bucketName: string): Promise<Response<boolean>> {
    const data = this.safePortfolioData;
    
    if (data.buckets[bucketName]) {
      return false;
    }
    
    data.buckets[bucketName] = [];
    
    runInAction(() => {
      this._portfolioData = toResource(data);
    });
    
    const persistResponse = await this.persistChanges();
    return isSuccess(persistResponse) ? true : persistResponse;
  }

  async removeBucket(bucketName: string): Promise<Response<boolean>> {
    const data = this.safePortfolioData;
    
    if (!data.buckets[bucketName]) {
      return false;
    }
    
    // Remove all assets in this bucket
    data.buckets[bucketName].forEach(assetId => {
      delete data.assets[assetId];
    });
    
    delete data.buckets[bucketName];
    
    runInAction(() => {
      this._portfolioData = toResource(data);
    });
    
    const persistResponse = await this.persistChanges();
    return isSuccess(persistResponse) ? true : persistResponse;
  }

  async addAsset(assetId: string, assetData: Asset): Promise<Response<boolean>> {
    const data = this.safePortfolioData;
    const uniqueKey = `${assetId}_${assetData.bucket}`;
    
    if (data.assets[uniqueKey]) {
      return false;
    }
    
    data.assets[uniqueKey] = assetData;
    data.buckets[assetData.bucket].push(uniqueKey);
    
    runInAction(() => {
      this._portfolioData = toResource(data);
    });

    // Fetch price if needed
    if (!priceStore.hasPrice(assetId)) {
      priceStore.fetchCurrentPrices([assetId]);
    }
    
    const persistResponse = await this.persistChanges();
    return isSuccess(persistResponse) ? true : persistResponse;
  }

  async updatePricesForAllAssets(): Promise<Response<void>> {
    const data = this.safePortfolioData;
    const uniqueAssetIds = Array.from(new Set(Object.values(data.assets).map(asset => asset.id)));
    
    if (uniqueAssetIds.length > 0) {
      return priceStore.fetchCurrentPrices(uniqueAssetIds);
    }
    
    return undefined;
  }

  // Legacy methods for compatibility with existing components
  assetExistsInBucket(assetId: string, bucketName: string): boolean {
    const data = this.safePortfolioData;
    if (!data.buckets[bucketName]) {
      return false;
    }
    const uniqueKey = `${assetId}_${bucketName}`;
    return data.buckets[bucketName].includes(uniqueKey);
  }

  async removeAsset(assetId: string): Promise<Response<boolean>> {
    const data = this.safePortfolioData;
    
    if (!data.assets[assetId]) {
      return false;
    }
    
    const asset = data.assets[assetId];
    const bucketAssets = data.buckets[asset.bucket];
    const index = bucketAssets.indexOf(assetId);
    
    if (index > -1) {
      bucketAssets.splice(index, 1);
    }
    
    delete data.assets[assetId];
    
    runInAction(() => {
      this._portfolioData = toResource(data);
    });
    
    const persistResponse = await this.persistChanges();
    return isSuccess(persistResponse) ? true : persistResponse;
  }

  async updateAsset(assetId: string, updates: Partial<Asset>): Promise<Response<boolean>> {
    const data = this.safePortfolioData;
    
    if (!data.assets[assetId]) {
      return false;
    }
    
    const asset = data.assets[assetId];
    const oldBucket = asset.bucket;
    
    // Apply updates
    Object.assign(asset, updates);
    asset.lastUpdated = new Date().toISOString();
    
    // Handle bucket change
    if (updates.bucket && updates.bucket !== oldBucket) {
      // Remove from old bucket
      const oldBucketAssets = data.buckets[oldBucket];
      const index = oldBucketAssets.indexOf(assetId);
      if (index > -1) {
        oldBucketAssets.splice(index, 1);
      }
      
      // Add to new bucket
      data.buckets[updates.bucket].push(assetId);
    }
    
    runInAction(() => {
      this._portfolioData = toResource(data);
    });
    
    const persistResponse = await this.persistChanges();
    return isSuccess(persistResponse) ? true : persistResponse;
  }

  async replaceAssetInBucket(assetId: string, newAssetData: Asset): Promise<Response<boolean>> {
    const data = this.safePortfolioData;
    const uniqueKey = `${assetId}_${newAssetData.bucket}`;
    
    if (!data.assets[uniqueKey]) {
      return false;
    }
    
    // Update the asset data
    data.assets[uniqueKey] = { 
      ...newAssetData, 
      lastUpdated: new Date().toISOString() 
    };
    
    runInAction(() => {
      this._portfolioData = toResource(data);
    });
    
    const persistResponse = await this.persistChanges();
    return isSuccess(persistResponse) ? true : persistResponse;
  }

  async incrementAssetInBucket(assetId: string, bucketName: string, incrementData: { amount: number; avgBuyPrice?: number }): Promise<Response<boolean>> {
    const data = this.safePortfolioData;
    const uniqueKey = `${assetId}_${bucketName}`;
    
    if (!data.assets[uniqueKey]) {
      return false;
    }
    
    const existingAsset = data.assets[uniqueKey];
    const currentAmount = existingAsset.amount;
    const incrementAmount = incrementData.amount;
    const newTotalAmount = currentAmount + incrementAmount;
    
    // Prevent negative amounts
    if (newTotalAmount <= 0) {
      return false;
    }
    
    // Calculate new weighted average price if both assets have avgBuyPrice
    let newAvgBuyPrice = existingAsset.avgBuyPrice;
    
    if (existingAsset.avgBuyPrice !== undefined && incrementData.avgBuyPrice !== undefined) {
      const existingValue = currentAmount * existingAsset.avgBuyPrice;
      const incrementValue = Math.abs(incrementAmount) * incrementData.avgBuyPrice;
      
      if (incrementAmount > 0) {
        // Adding to position
        const totalValue = existingValue + incrementValue;
        newAvgBuyPrice = totalValue / newTotalAmount;
      } else {
        // Reducing position - keep existing avg buy price
        newAvgBuyPrice = existingAsset.avgBuyPrice;
      }
    } else if (incrementAmount > 0 && incrementData.avgBuyPrice !== undefined && existingAsset.avgBuyPrice === undefined) {
      // If we're adding with a price but existing has no price, set the new price
      newAvgBuyPrice = incrementData.avgBuyPrice;
    }
    
    // Update the asset
    data.assets[uniqueKey] = {
      ...existingAsset,
      amount: newTotalAmount,
      avgBuyPrice: newAvgBuyPrice,
      lastUpdated: new Date().toISOString()
    };
    
    runInAction(() => {
      this._portfolioData = toResource(data);
    });
    
    const persistResponse = await this.persistChanges();
    return isSuccess(persistResponse) ? true : persistResponse;
  }

  // Portfolio calculation methods
  get totalPortfolioValue(): number {
    return this.assetDisplayData.reduce((total, asset) => total + asset.marketValue, 0);
  }

  get totalCostBasis(): number {
    return this.assetDisplayData.reduce((total, asset) => total + asset.costBasis, 0);
  }

  get totalPnL(): number {
    return this.totalPortfolioValue - this.totalCostBasis;
  }

  get totalPnLPercentage(): number {
    if (this.totalCostBasis === 0) return 0;
    return (this.totalPnL / this.totalCostBasis) * 100;
  }

  get assetDisplayData(): AssetDisplayData[] {
    const data = this.safePortfolioData;
    const assets = Object.values(data.assets);
    const totalValue = assets.reduce((sum, asset) => {
      const currentPrice = priceStore.getPrice(asset.id);
      return sum + (asset.amount * currentPrice);
    }, 0);

    return assets.map(asset => {
      const currentPrice = priceStore.getPrice(asset.id);
      const marketValue = asset.amount * currentPrice;
      const costBasis = asset.avgBuyPrice ? asset.amount * asset.avgBuyPrice : 0;
      const pnl = marketValue - costBasis;
      const pnlPercentage = costBasis > 0 ? (pnl / costBasis) * 100 : undefined;
      const allocation = totalValue > 0 ? (marketValue / totalValue) * 100 : 0;

      return {
        ...asset,
        currentPrice,
        marketValue,
        costBasis,
        pnl,
        pnlPercentage,
        allocation,
      };
    });
  }

  get bucketDisplayData(): BucketDisplayData[] {
    const data = this.safePortfolioData;
    const buckets = Object.keys(data.buckets);
    const totalValue = this.totalPortfolioValue;

    return buckets.map(bucketName => {
      const bucketAssets = this.getAssetsByBucket(bucketName);
      const bucketAssetsDisplay = bucketAssets.map(asset => {
        const currentPrice = priceStore.getPrice(asset.id);
        const marketValue = asset.amount * currentPrice;
        const costBasis = asset.avgBuyPrice ? asset.amount * asset.avgBuyPrice : 0;
        const pnl = marketValue - costBasis;
        const pnlPercentage = costBasis > 0 ? (pnl / costBasis) * 100 : undefined;
        const allocation = totalValue > 0 ? (marketValue / totalValue) * 100 : 0;

        return {
          ...asset,
          currentPrice,
          marketValue,
          costBasis,
          pnl,
          pnlPercentage,
          allocation,
        };
      });

      const bucketValue = bucketAssetsDisplay.reduce((sum, asset) => sum + asset.marketValue, 0);
      const bucketAllocation = totalValue > 0 ? (bucketValue / totalValue) * 100 : 0;

      return {
        name: bucketName,
        value: bucketValue,
        allocation: bucketAllocation,
        assets: bucketAssetsDisplay,
      };
    });
  }
}

export const portfolioStore = new PortfolioStore();
